'use client';

import { useState, useCallback } from 'react';
import type { ChatMessage, BugReport } from '../context/types';

interface Props {
  message: ChatMessage;
  onViewBugReport?: (report: BugReport) => void;
  onViewAgentPrompt?: (prompt: string) => void;
}

/** Format a Unix ms timestamp as HH:MM */
function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Render content with simple inline code and code-block support.
 * Handles `backtick` spans and triple-backtick fenced blocks.
 */
function MessageContent({ content }: { content: string }) {
  // Split on fenced code blocks first
  const parts = content.split(/(```[\s\S]*?```)/g);

  return (
    <span className="whitespace-pre-wrap break-words">
      {parts.map((part, i) => {
        if (part.startsWith('```')) {
          const inner = part.replace(/^```[^\n]*\n?/, '').replace(/```$/, '');
          return (
            <pre
              key={i}
              className="bg-surface-card mt-1 overflow-x-auto rounded p-2 font-mono text-xs"
            >
              {inner}
            </pre>
          );
        }

        // Handle inline backtick spans
        const inlineParts = part.split(/(`[^`]+`)/g);
        return (
          <span key={i}>
            {inlineParts.map((inline, j) => {
              if (inline.startsWith('`') && inline.endsWith('`')) {
                return (
                  <code
                    key={j}
                    className="bg-surface-card rounded px-1 py-0.5 font-mono text-xs"
                  >
                    {inline.slice(1, -1)}
                  </code>
                );
              }
              // Render **bold** text
              const boldParts = inline.split(/(\*\*[^*]+\*\*)/g);
              return (
                <span key={j}>
                  {boldParts.map((b, k) => {
                    if (b.startsWith('**') && b.endsWith('**')) {
                      return <strong key={k}>{b.slice(2, -2)}</strong>;
                    }
                    return <span key={k}>{b}</span>;
                  })}
                </span>
              );
            })}
          </span>
        );
      })}
    </span>
  );
}

export function DebugChatMessage({
  message,
  onViewBugReport,
  onViewAgentPrompt,
}: Props) {
  const isUser = message.role === 'user';
  const hasBugReport = Boolean(message.metadata?.bugReport);
  const hasAgentPrompt = Boolean(message.metadata?.agentPrompt);

  return (
    <div
      className={`flex flex-col gap-1 ${isUser ? 'items-end' : 'items-start'}`}
    >
      <div
        className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
          isUser
            ? 'bg-accent/20 text-text-primary'
            : 'bg-surface-2 text-text-primary'
        }`}
      >
        <MessageContent content={message.content} />
      </div>

      {/* Action buttons for assistant messages with metadata */}
      {!isUser && (hasBugReport || hasAgentPrompt) && (
        <div className="flex gap-2">
          {hasBugReport && onViewBugReport && (
            <button
              type="button"
              onClick={() => onViewBugReport(message.metadata!.bugReport!)}
              className="text-accent hover:text-accent/80 rounded px-2 py-1 text-xs underline transition-colors"
            >
              View Bug Report
            </button>
          )}
          {hasAgentPrompt && onViewAgentPrompt && (
            <button
              type="button"
              onClick={() => onViewAgentPrompt(message.metadata!.agentPrompt!)}
              className="text-accent hover:text-accent/80 rounded px-2 py-1 text-xs underline transition-colors"
            >
              View Agent Prompt
            </button>
          )}
        </div>
      )}

      {/* Timestamp */}
      <span className="text-text-tertiary px-1 text-[10px]">
        {formatTime(message.timestamp)}
      </span>
    </div>
  );
}
